import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
	ActivityIndicator,
	Image,
	ImageSourcePropType,
	Platform,
	StyleSheet,
	Text,
	TextInput,
	View,
} from 'react-native';
import Svg, { Circle, Defs, G, LinearGradient, Path, Rect, Stop } from 'react-native-svg';

import VipCrown from '../../../assets/profileRedesign/vip-crown.svg';
import { goldGlow } from '../../../shared/glowStyles';
import { ACHIEVEMENTS_FONT } from '../Achievements/achievementsTheme';
import { Pressable } from '../common/Touchable';
import { DESKTOP_TWIN_SCALE } from '../Modals/useRedesignScale';
import { BuyUsernameChangerButton, LockGlyph } from './UsernameLock';

/**
 * Figma "Edit Profile / Desktop — Screen States" (5605:102663) on page "PAVEL DRAFT": the edit
 * modal on the 1440x1024 desktop frame, drawn with the "/ Desktop" twins of the mobile components
 * at 1.25x. Every number below is read straight off that artboard, so `u()` turns it into screen
 * pixels at the modal's current scale.
 *
 * Unlike the mobile panel this one is a flex column: the modal grows with its content (the
 * description wrapping to three lines grows it from 787 to 848), and the owner sizes the panel
 * from `onContentHeight`.
 */
/** The Default state's modal height on the artboard, in mobile design units. */
export const DESKTOP_PANEL_DESIGN_HEIGHT = 787.25 / DESKTOP_TWIN_SCALE;

export type EditFieldKey = 'username' | 'age' | 'location' | 'description';
/**
 * The field states the artboard draws: Default (at rest), Empty (no value), Focused (typing),
 * Unsaved (edited, not committed), Error (the submit came back with the username taken) and
 * Locked (the free renames are spent; the username shows read-only with a buy CTA).
 */
export type EditFieldState = 'default' | 'empty' | 'focused' | 'unsaved' | 'error' | 'locked';

type Props = {
	scale: number;
	avatarSource: ImageSourcePropType;
	isVip: boolean;
	values: Record<EditFieldKey, string>;
	fieldStates: Record<EditFieldKey, EditFieldState>;
	onChangeField: (key: EditFieldKey, value: string) => void;
	onFocusField: (key: EditFieldKey) => void;
	onBlurField: (key: EditFieldKey) => void;
	usernameHelper: { text: string; danger: boolean };
	onBuyUsernameChanger: () => void;
	descriptionLimit: number;
	editable: boolean;
	isSaving: boolean;
	isSaveInert: boolean;
	isCancelLive: boolean;
	submitError?: string;
	onBack: () => void;
	onClose: () => void;
	onSave: () => void;
	onChangePhoto: () => void;
	/** "Remaining avatar changes: N" under the avatar; omitted while the quota is unknown. */
	avatarChangesNote?: string;
	/** The free avatar changes are spent. */
	isChangePhotoDisabled?: boolean;
	/** The column's natural height, in the mobile design units the panel geometry takes. */
	onContentHeight: (designHeight: number) => void;
};

const COLORS = {
	surfaceRaised: '#330E07',
	surfaceSunken: '#0F0301',
	surfaceOverlay: '#4A1A0E',
	borderSubtle: '#5C2416',
	borderOrange: '#DA600E',
	borderGold: '#E89200',
	gold50: '#FFF5B9',
	gold300: '#FFC500',
	textSecondary: '#D9C4BC',
	textMuted: '#A88E86',
	textOnGold: '#2A0A04',
	danger: '#FF4D3D',
	headerGlyph: '#FFFF99',
	cameraRim: '#FFF908',
	avatarRing: '#FFE46D',
	saveRim: '#FFE45C',
} as const;

/** "Profile Icon / *" gradients: base gold at rest, lifted 35% toward white when active, dimmed when empty. */
const ICON_TONES = {
	base: ['#B25200', '#F58F00', '#FFF908', '#FFFF9E'],
	lifted: ['#CD8F59', '#F9B659', '#FFFB5E', '#FFFFC0'],
	dim: ['#803B00', '#B06700', '#B8B306', '#B8B872'],
	danger: ['#FF4D3D', '#FF4D3D', '#FF4D3D', '#FF4D3D'],
	muted: ['#A88E86', '#A88E86', '#A88E86', '#A88E86'],
} as const;
type IconTone = keyof typeof ICON_TONES;
/** The pencil's centre line is drawn in a solid that follows the tone. */
const PENCIL_LINE: Record<IconTone, string> = {
	base: '#C88200',
	lifted: '#DBAE59',
	dim: '#905E00',
	danger: '#FF4D3D',
	muted: '#A88E86',
};

/** Per state: the field's rim and bloom, the input's fill and rim, and the label icon's tone and glow. */
const FIELD_LOOK: Record<
	EditFieldState,
	{
		rim: number;
		bloom?: { color: string; opacity: number; radius: number };
		inputFill: string;
		inputRim: string;
		icon: IconTone;
		iconGlow: string;
	}
> = {
	default: {
		rim: 1.25,
		inputFill: COLORS.surfaceRaised,
		inputRim: COLORS.borderSubtle,
		icon: 'base',
		iconGlow: 'drop-shadow(0 0 1.75px rgba(244,106,12,0.75))',
	},
	empty: {
		rim: 1.25,
		inputFill: COLORS.surfaceSunken,
		inputRim: COLORS.borderSubtle,
		icon: 'dim',
		iconGlow: 'drop-shadow(0 0 1.5px rgba(244,106,12,0.3))',
	},
	focused: {
		rim: 1.875,
		bloom: { color: '#FFB21A', opacity: 0.45, radius: 7.5 },
		inputFill: COLORS.surfaceOverlay,
		inputRim: COLORS.borderGold,
		icon: 'lifted',
		iconGlow: 'drop-shadow(0 0 2.25px rgba(255,178,26,0.9))',
	},
	unsaved: {
		rim: 1.875,
		bloom: { color: '#FFB21A', opacity: 0.28, radius: 6.25 },
		inputFill: COLORS.surfaceOverlay,
		inputRim: COLORS.borderSubtle,
		icon: 'lifted',
		iconGlow: 'drop-shadow(0 0 2px rgba(255,178,26,0.55))',
	},
	error: {
		rim: 1.875,
		bloom: { color: COLORS.danger, opacity: 0.32, radius: 6.25 },
		inputFill: COLORS.surfaceOverlay,
		inputRim: COLORS.danger,
		icon: 'danger',
		iconGlow: 'drop-shadow(0 0 2.5px rgba(255,77,61,0.6))',
	},
	locked: {
		rim: 1.25,
		inputFill: COLORS.surfaceSunken,
		inputRim: COLORS.borderSubtle,
		icon: 'muted',
		iconGlow: 'none',
	},
};

const INPUT_LINE_HEIGHT = 30;
/** The panel's own rim, drawn by RedesignModal at 1x the mobile scale. */
const PANEL_RIM = 1.25;
const DESCRIPTION_MAX_LINES = 3;

const ProfileRedesignEditDesktop = ({
	scale,
	avatarSource,
	isVip,
	values,
	fieldStates,
	onChangeField,
	onFocusField,
	onBlurField,
	usernameHelper,
	onBuyUsernameChanger,
	descriptionLimit,
	editable,
	isSaving,
	isSaveInert,
	isCancelLive,
	submitError,
	onBack,
	onClose,
	onSave,
	onChangePhoto,
	avatarChangesNote,
	isChangePhotoDisabled = false,
	onContentHeight,
}: Props) => {
	const { t } = useTranslation();
	const u = (n: number) => (n * scale) / DESKTOP_TWIN_SCALE;
	const styles = createStyles(u);
	const [descriptionLines, setDescriptionLines] = useState(1);
	const isDescriptionAtCap = values.description.length >= descriptionLimit;

	const fieldBox = (key: EditFieldKey) => {
		const look = FIELD_LOOK[fieldStates[key]];
		return [
			styles.field,
			{ borderWidth: u(look.rim) },
			look.bloom && {
				shadowColor: look.bloom.color,
				shadowOpacity: look.bloom.opacity,
				shadowRadius: u(look.bloom.radius),
				shadowOffset: { width: 0, height: 0 },
			},
		];
	};

	const label = (key: EditFieldKey, text: string, trailing?: React.ReactNode) => {
		const look = FIELD_LOOK[fieldStates[key]];
		return (
			<View style={styles.labelRow}>
				<View
					style={[styles.labelIcon, Platform.OS === 'web' && ({ filter: look.iconGlow } as any)]}
				>
					<FieldIcon kind={key} tone={look.icon} size={u(17.5)} />
				</View>
				<Text style={[styles.label, fieldStates[key] === 'locked' && styles.mutedLabel]}>
					{text}
				</Text>
				<View style={styles.spacer} />
				{trailing}
			</View>
		);
	};

	const input = (
		key: EditFieldKey,
		placeholder: string,
		extra: Partial<React.ComponentProps<typeof TextInput>> = {},
		trailing?: React.ReactNode,
	) => {
		const look = FIELD_LOOK[fieldStates[key]];
		return (
			<View
				style={[styles.inputBox, { backgroundColor: look.inputFill, borderColor: look.inputRim }]}
			>
				<TextInput
					value={values[key]}
					onChangeText={value => onChangeField(key, value)}
					onFocus={() => onFocusField(key)}
					onBlur={() => onBlurField(key)}
					editable={editable}
					placeholder={placeholder}
					placeholderTextColor={COLORS.textMuted}
					selectionColor={COLORS.gold300}
					style={[styles.inputText, Platform.OS === 'web' && styles.webInputReset]}
					{...extra}
				/>
				{trailing}
			</View>
		);
	};

	const descriptionHeight = u(INPUT_LINE_HEIGHT) * descriptionLines;

	return (
		<View
			style={styles.column}
			onLayout={event =>
				onContentHeight((event.nativeEvent.layout.height + 2 * u(PANEL_RIM)) / scale)
			}
		>
			<View style={styles.header}>
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_BACK_TO_PROFILE', { defaultValue: 'Back to profile' })}
					onPress={onBack}
					style={styles.backButton}
				>
					<Svg width={u(30)} height={u(30)} viewBox='0 0 30 30'>
						<Path
							d='M18.75 5.625L9.375 15L18.75 24.375'
							fill='none'
							stroke={COLORS.headerGlyph}
							strokeWidth={2.8125}
							strokeLinecap='round'
						/>
					</Svg>
				</Pressable>
				<Text style={styles.title}>
					{t('T_SITE_PROFILE_EDIT', { defaultValue: 'Edit Profile' })}
				</Text>
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_CLOSE_EDIT_PROFILE', {
						defaultValue: 'Close edit profile',
					})}
					onPress={onClose}
					style={styles.closeButton}
				>
					<Svg width={u(30)} height={u(30)} viewBox='0 0 30 30'>
						<Path
							d='M6.564 6.564L23.436 23.436M23.436 6.564L6.564 23.436'
							fill='none'
							stroke={COLORS.headerGlyph}
							strokeWidth={2.8125}
							strokeLinecap='round'
						/>
					</Svg>
				</Pressable>
			</View>

			<View style={styles.avatarArea}>
				<View style={styles.avatarRing}>
					<Image source={avatarSource} resizeMode='cover' style={styles.avatar} />
				</View>
				{isVip ? <VipCrown width={u(49.5)} height={u(43.3)} style={styles.crown} /> : null}
				<Pressable
					accessibilityRole='button'
					accessibilityLabel={t('T_SITE_A11Y_CHANGE_PROFILE_PHOTO', {
						defaultValue: 'Change profile photo',
					})}
					onPress={onChangePhoto}
					disabled={isChangePhotoDisabled}
					style={[styles.cameraButton, isChangePhotoDisabled && styles.cameraLocked]}
				>
					<CameraGlyph width={u(27.5)} height={u(20)} />
				</Pressable>
				{avatarChangesNote ? (
					<Text numberOfLines={1} style={[styles.helper, styles.avatarNote]}>
						{avatarChangesNote}
					</Text>
				) : null}
			</View>

			<View style={styles.bodyGrid}>
				<View style={fieldBox('username')}>
					{label('username', t('T_SITE_PROFILE_MODAL_USERNAME', { defaultValue: 'Username' }))}
					{fieldStates.username === 'locked' ? (
						<View
							style={[
								styles.inputBox,
								{
									backgroundColor: FIELD_LOOK.locked.inputFill,
									borderColor: FIELD_LOOK.locked.inputRim,
								},
							]}
						>
							<Text numberOfLines={1} style={[styles.inputText, styles.lockedValue]}>
								{values.username}
							</Text>
							<LockGlyph size={u(17.5)} />
						</View>
					) : (
						input(
							'username',
							t('T_SITE_PROFILE_MODAL_USERNAME', { defaultValue: 'Username' }),
							{
								autoCapitalize: 'none',
								autoCorrect: false,
								maxLength: 16,
								onSubmitEditing: () => {
									if (!isSaveInert && !isSaving) onSave();
								},
							},
							fieldStates.username === 'error' ? <AlertGlyph size={u(17.5)} /> : null,
						)
					)}
					<Text style={[styles.helper, usernameHelper.danger && styles.dangerText]}>
						{usernameHelper.text}
					</Text>
					{fieldStates.username === 'locked' ? (
						<BuyUsernameChangerButton
							onPress={onBuyUsernameChanger}
							height={u(55)}
							radius={u(12.5)}
							rim={u(1.25)}
							glow={u(11.25)}
							fontSize={u(22)}
							letterSpacing={u(0.44)}
						/>
					) : null}
				</View>

				<View style={styles.asymmetricRow}>
					<View style={[fieldBox('age'), styles.ageField]}>
						{label('age', t('T_SITE_PROFILE_MODAL_AGE', { defaultValue: 'Age' }))}
						{input('age', t('T_SITE_PROFILE_MODAL_AGE', { defaultValue: 'Age' }), {
							keyboardType: 'number-pad',
							maxLength: 2,
						})}
					</View>
					<View style={[fieldBox('location'), styles.locationField]}>
						{label('location', t('T_SITE_PROFILE_MODAL_LOCATION', { defaultValue: 'Location' }))}
						{input(
							'location',
							t('T_SITE_PROFILE_LOCATION_PLACEHOLDER', { defaultValue: 'Add your location' }),
							{ maxLength: 30 },
						)}
					</View>
				</View>

				<View style={fieldBox('description')}>
					{label(
						'description',
						t('T_SITE_PROFILE_DESCRIPTION', { defaultValue: 'Description' }),
						<Text style={[styles.counter, isDescriptionAtCap && styles.dangerText]}>
							{values.description.length}/{descriptionLimit}
						</Text>,
					)}
					<View
						style={[
							styles.inputBox,
							{
								backgroundColor: FIELD_LOOK[fieldStates.description].inputFill,
								borderColor: FIELD_LOOK[fieldStates.description].inputRim,
							},
						]}
					>
						{/* A hidden copy of the text measures how many lines it wraps to, so the
						    field grows to at most three lines instead of scrolling inside itself. */}
						<Text
							pointerEvents='none'
							style={[styles.inputFont, styles.measure]}
							onLayout={event =>
								setDescriptionLines(
									Math.min(
										DESCRIPTION_MAX_LINES,
										Math.max(1, Math.round(event.nativeEvent.layout.height / u(INPUT_LINE_HEIGHT))),
									),
								)
							}
						>
							{values.description || ' '}
						</Text>
						<TextInput
							value={values.description}
							onChangeText={value => onChangeField('description', value)}
							onFocus={() => onFocusField('description')}
							onBlur={() => onBlurField('description')}
							editable={editable}
							maxLength={descriptionLimit}
							multiline
							scrollEnabled={false}
							placeholder={t('T_SITE_PROFILE_DESCRIPTION_PLACEHOLDER', {
								defaultValue: 'Say something about yourself',
							})}
							placeholderTextColor={COLORS.textMuted}
							selectionColor={COLORS.gold300}
							textAlignVertical='top'
							style={[
								styles.inputText,
								{ height: descriptionHeight },
								Platform.OS === 'web' && styles.webInputReset,
							]}
						/>
					</View>
				</View>
			</View>

			{submitError ? (
				<Text style={[styles.submitError, styles.dangerText]}>{submitError}</Text>
			) : null}

			<View style={styles.actions}>
				<Pressable
					accessibilityRole='button'
					onPress={onBack}
					disabled={!isCancelLive || isSaving}
					style={[
						styles.action,
						isCancelLive ? styles.cancelLive : styles.actionInert,
						isSaving && styles.actionBusy,
					]}
				>
					<Text style={[styles.actionLabel, isCancelLive ? styles.cancelLabel : styles.inertLabel]}>
						{t('T_SITE_MODAL_NEW_ROOM_CANCEL', { defaultValue: 'Cancel' })}
					</Text>
				</Pressable>
				<Pressable
					accessibilityRole='button'
					onPress={onSave}
					disabled={isSaveInert || isSaving}
					style={[styles.action, isSaveInert ? styles.actionInert : styles.saveLive]}
				>
					{!isSaveInert ? (
						<View pointerEvents='none' style={[StyleSheet.absoluteFill, styles.saveFill]}>
							<Svg width='100%' height='100%'>
								<Defs>
									<LinearGradient id='editProfileDesktopSave' x1='0' y1='0' x2='0' y2='1'>
										<Stop offset='0' stopColor='#FFF5B9' />
										<Stop offset='0.35' stopColor='#FFEE82' />
										<Stop offset='0.78' stopColor='#F5A300' />
										<Stop offset='1' stopColor='#CE7C00' />
									</LinearGradient>
								</Defs>
								<Rect x='0' y='0' width='100%' height='100%' fill='url(#editProfileDesktopSave)' />
							</Svg>
						</View>
					) : null}
					{isSaving && !isSaveInert ? (
						<ActivityIndicator color={COLORS.textOnGold} size='small' />
					) : (
						<Text style={[styles.actionLabel, isSaveInert ? styles.inertLabel : styles.saveLabel]}>
							{t('T_SITE_PROFILE_MODAL_SAVE', { defaultValue: 'Save' })}
						</Text>
					)}
				</Pressable>
			</View>
		</View>
	);
};

/** "Icon / Camera" (Layer_1), 27.5x20, centred in the change-photo button. */
const CameraGlyph = ({ width, height }: { width: number; height: number }) => (
	<Svg width={width} height={height} viewBox='0 0 27.5 20'>
		<Path
			fillRule='evenodd'
			clipRule='evenodd'
			fill={COLORS.headerGlyph}
			d='M13.7492 7.21747C14.8989 7.21747 15.9407 7.6683 16.6934 8.39505C17.4468 9.12181 17.9142 10.126 17.9142 11.235C17.9142 12.3439 17.4468 13.3444 16.6934 14.0726H16.691C15.9376 14.8016 14.8966 15.2502 13.7492 15.2502C12.6019 15.2502 11.5601 14.8016 10.8074 14.0726C10.054 13.3459 9.58663 12.3417 9.58663 11.235C9.58663 10.1283 10.054 9.12331 10.8074 8.39732V8.39505C11.5632 7.6683 12.6019 7.21747 13.7492 7.21747ZM20.7059 11.235C20.7059 14.9403 17.5914 17.9431 13.7492 17.9431C9.90707 17.9431 6.79489 14.9403 6.79489 11.235C6.79489 7.52959 9.90785 4.52457 13.7492 4.52457C17.5906 4.52457 20.7059 7.52959 20.7059 11.235ZM20.5527 2.41518V2.12041C20.5527 1.64621 20.7544 1.21649 21.0779 0.904383C21.4015 0.592272 21.847 0.397768 22.3386 0.397768H24.2237C24.7153 0.397768 25.1631 0.592272 25.4867 0.904383C25.8103 1.21649 26.0096 1.64621 26.0096 2.12041V2.6421C25.9033 2.61044 25.797 2.58255 25.6907 2.55842C25.2522 2.46042 24.8044 2.41518 24.4105 2.41518H20.5535H20.5527ZM3.09029 2.92632H7.39747L8.06415 1.41552C8.22827 1.04159 8.55184 0.680477 8.93246 0.421892C9.30448 0.167831 9.74763 0.0012207 10.1689 0.0012207H17.3311C17.7524 0.0012207 18.1932 0.167831 18.5675 0.421892C18.9489 0.682739 19.2717 1.04385 19.4359 1.41552L20.1025 2.92632H24.4097C25.0834 2.92632 25.9228 3.0673 26.541 3.40504C27.1061 3.71489 27.5 4.17854 27.5 4.83668V17.631C27.5 18.2846 27.0858 18.8576 26.5074 19.279C25.8978 19.7215 25.0803 20.0012 24.4097 20.0012H3.09029C2.41659 20.0012 1.60142 19.7215 0.992582 19.279C0.414227 18.8598 0 18.2854 0 17.631V4.83668C0 4.17854 0.394688 3.71489 0.958975 3.40504C1.57719 3.0673 2.41659 2.92632 3.09029 2.92632Z'
		/>
	</Svg>
);

/** "Profile Icon / User | Calendar | Location | Edit", 17.5 square, filled with the tone's gradient. */
const FieldIcon = ({ kind, tone, size }: { kind: EditFieldKey; tone: IconTone; size: number }) => {
	const stops = ICON_TONES[tone];
	const id = `editProfileIcon-${kind}-${tone}`;
	const gradient = (suffix: string, y1: number, y2: number) => (
		<LinearGradient
			id={`${id}-${suffix}`}
			x1='8.75'
			y1={y1}
			x2='8.75'
			y2={y2}
			gradientUnits='userSpaceOnUse'
		>
			<Stop offset='0' stopColor={stops[0]} />
			<Stop offset='0.08' stopColor={stops[1]} />
			<Stop offset='0.6' stopColor={stops[2]} />
			<Stop offset='1' stopColor={stops[3]} />
		</LinearGradient>
	);

	return (
		<Svg width={size} height={size} viewBox='0 0 17.5 17.5'>
			{kind === 'username' ? (
				<>
					<Defs>
						{gradient('a', 8.75, 2.1875)}
						{gradient('b', 15.8594, 8.75)}
					</Defs>
					<Circle cx='8.75' cy='5.46875' r='3.28125' fill={`url(#${id}-a)`} />
					<Path
						d='M2.1875 15.8594V14.2188C2.1875 12.7683 2.8789 11.3773 4.10961 10.3518C5.34032 9.32617 7.00952 8.75 8.75 8.75C10.4905 8.75 12.1597 9.32617 13.3904 10.3518C14.6211 11.3773 15.3125 12.7683 15.3125 14.2188V15.8594H2.1875Z'
						fill={`url(#${id}-b)`}
					/>
				</>
			) : null}
			{kind === 'age' ? (
				<>
					<Defs>
						{gradient('a', 6.3, 2.1)}
						{gradient('b', 17.5, 7)}
					</Defs>
					<Path
						d='M15.0006 3.42411H14.0451V2.41042C14.0451 2.2389 13.9097 2.1 13.7424 2.1H12.5675C12.4003 2.1 12.2648 2.2389 12.2648 2.41042V3.42411H9.64003V2.41042C9.64003 2.2389 9.50456 2.1 9.33727 2.1H8.16244C7.99515 2.1 7.85967 2.2389 7.85967 2.41042V3.42411H5.23492V2.41042C5.23492 2.2389 5.09945 2.1 4.93216 2.1H3.75732C3.59004 2.1 3.45456 2.2389 3.45456 2.41042V3.42411H2.49914C1.89214 3.42411 1.4 3.92899 1.4 4.55164V6.3H16.1V4.55164C16.1 3.92899 15.6079 3.42441 15.0006 3.42441V3.42411Z'
						fill={`url(#${id}-a)`}
					/>
					<Path
						d='M1.4 7V16.322C1.4 16.9727 1.89214 17.5 2.49944 17.5H15.0006C15.6079 17.5 16.1 16.9727 16.1 16.322V7H1.4Z'
						fill={`url(#${id}-b)`}
					/>
					<Path
						d='M4.2 10.6167C4.2 10.1656 4.56563 9.8 5.01667 9.8H5.11274C5.56378 9.8 5.92941 10.1656 5.92941 10.6167C5.92941 11.0677 5.56378 11.4333 5.11274 11.4333H5.01667C4.56563 11.4333 4.2 11.0677 4.2 10.6167ZM8.23529 10.6167C8.23529 10.1656 8.60093 9.8 9.05196 9.8H9.14804C9.59907 9.8 9.96471 10.1656 9.96471 10.6167C9.96471 11.0677 9.59907 11.4333 9.14804 11.4333H9.05196C8.60093 11.4333 8.23529 11.0677 8.23529 10.6167ZM12.2706 10.6167C12.2706 10.1656 12.6362 9.8 13.0873 9.8H13.1833C13.6344 9.8 14 10.1656 14 10.6167C14 11.0677 13.6344 11.4333 13.1833 11.4333H13.0873C12.6362 11.4333 12.2706 11.0677 12.2706 10.6167ZM4.2 13.8833C4.2 13.4323 4.56563 13.0667 5.01667 13.0667H5.11274C5.56378 13.0667 5.92941 13.4323 5.92941 13.8833C5.92941 14.3344 5.56378 14.7 5.11274 14.7H5.01667C4.56563 14.7 4.2 14.3344 4.2 13.8833ZM8.23529 13.8833C8.23529 13.4323 8.60093 13.0667 9.05196 13.0667H9.14804C9.59907 13.0667 9.96471 13.4323 9.96471 13.8833C9.96471 14.3344 9.59907 14.7 9.14804 14.7H9.05196C8.60093 14.7 8.23529 14.3344 8.23529 13.8833ZM12.2706 13.8833C12.2706 13.4323 12.6362 13.0667 13.0873 13.0667H13.1833C13.6344 13.0667 14 13.4323 14 13.8833C14 14.3344 13.6344 14.7 13.1833 14.7H13.0873C12.6362 14.7 12.2706 14.3344 12.2706 13.8833Z'
						fill='#5B2403'
					/>
				</>
			) : null}
			{kind === 'location' ? (
				<>
					<Defs>{gradient('a', 16.9531, 1.09375)}</Defs>
					<Path
						fillRule='evenodd'
						clipRule='evenodd'
						d='M8.75 1.09375C4.92188 1.09375 2.1875 3.82812 2.1875 7.65625C2.1875 11.4844 8.75 16.9531 8.75 16.9531C8.75 16.9531 15.3125 11.4844 15.3125 7.65625C15.3125 3.82812 12.5781 1.09375 8.75 1.09375ZM8.75 4.375C8.0248 4.375 7.3293 4.66309 6.8165 5.17588C6.30371 5.68867 6.01563 6.38417 6.01562 7.10938C6.01563 7.83458 6.30371 8.53008 6.8165 9.04287C7.3293 9.55566 8.0248 9.84375 8.75 9.84375C9.4752 9.84375 10.1707 9.55566 10.6835 9.04287C11.1963 8.53008 11.4844 7.83458 11.4844 7.10938C11.4844 6.38417 11.1963 5.68867 10.6835 5.17588C10.1707 4.66309 9.4752 4.375 8.75 4.375Z'
						fill={`url(#${id}-a)`}
					/>
				</>
			) : null}
			{kind === 'description' ? (
				<>
					<Defs>
						{gradient('a', 14.7656, 2.73438)}
						{gradient('b', 15.8594, 12.0312)}
						{gradient('c', 4.92188, 0.683594)}
					</Defs>
					<Path
						d='M2.73438 12.0312L5.46875 14.7656L14.7656 5.46875L12.0312 2.73438L2.73438 12.0312Z'
						fill={`url(#${id}-a)`}
					/>
					<Path
						d='M1.64062 15.8594L5.46875 14.7656L2.73438 12.0312L1.64062 15.8594Z'
						fill={`url(#${id}-b)`}
					/>
					<Path
						d='M12.5781 2.1875L13.6719 1.09375C14.2188 0.546875 14.7656 0.546875 15.3125 1.09375L16.4062 2.1875C16.9531 2.73438 16.9531 3.28125 16.4062 3.82812L15.3125 4.92188L12.5781 2.1875Z'
						fill={`url(#${id}-c)`}
					/>
					<Path d='M3.82812 12.5781L12.0312 4.375' stroke={PENCIL_LINE[tone]} strokeWidth={1.5} />
				</>
			) : null}
		</Svg>
	);
};

/** "Status Icon / Alert", right-aligned in the input when the username is rejected. */
const AlertGlyph = ({ size }: { size: number }) => (
	<Svg width={size} height={size} viewBox='0 0 17.5 17.5'>
		<G>
			<Circle cx='8.75' cy='8.75' r='6.836' fill='none' stroke={COLORS.danger} strokeWidth={1.64} />
			<Path
				d='M8.75 5.195V9.734'
				stroke={COLORS.danger}
				strokeWidth={1.914}
				strokeLinecap='round'
			/>
			<Circle cx='8.75' cy='12.523' r='1.039' fill={COLORS.danger} />
		</G>
	</Svg>
);

const createStyles = (u: (n: number) => number) => {
	const inputFont = {
		color: COLORS.gold50,
		fontFamily: ACHIEVEMENTS_FONT,
		fontSize: u(20),
		fontWeight: '500',
		lineHeight: u(INPUT_LINE_HEIGHT),
	} as const;

	return StyleSheet.create({
		column: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: 0,
			// Figma strokes sit inside the frame, but here the panel's 1.25 rim takes space, so the
			// padding gives that back to keep the fields 417.5 wide.
			paddingTop: u(20 - PANEL_RIM),
			paddingHorizontal: u(15 - PANEL_RIM),
			paddingBottom: u(25 - PANEL_RIM),
			rowGap: u(20),
		},
		header: {
			height: u(40),
			flexDirection: 'row',
			alignItems: 'center',
			justifyContent: 'space-between',
		},
		backButton: {
			width: u(40),
			height: u(40),
			borderRadius: u(20),
			backgroundColor: COLORS.surfaceSunken,
			alignItems: 'center',
			justifyContent: 'center',
		},
		closeButton: {
			width: u(40),
			height: u(40),
			alignItems: 'center',
			justifyContent: 'center',
		},
		title: {
			color: COLORS.gold50,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: u(30),
			fontWeight: '700',
			lineHeight: u(39),
			includeFontPadding: false,
		},
		// The artboard's 203.75, plus the avatar-changes note under the camera button.
		avatarArea: { height: u(230) },
		avatarRing: {
			position: 'absolute',
			left: u(126.25),
			top: u(33.75),
			width: u(165),
			height: u(165),
			borderRadius: u(82.5),
			borderWidth: u(5),
			borderColor: COLORS.avatarRing,
			backgroundColor: '#190301',
			overflow: 'hidden',
			...goldGlow,
		},
		avatar: { width: '100%', height: '100%' },
		crown: { position: 'absolute', top: u(5), left: u(184), zIndex: 2 },
		cameraButton: {
			position: 'absolute',
			left: u(243.75),
			top: u(137.5),
			width: u(67.5),
			height: u(67.5),
			borderRadius: u(33.75),
			backgroundColor: COLORS.surfaceSunken,
			borderWidth: u(1.25),
			borderColor: COLORS.cameraRim,
			alignItems: 'center',
			justifyContent: 'center',
			...goldGlow,
			zIndex: 3,
		},
		cameraLocked: { opacity: 0.45 },
		avatarNote: {
			position: 'absolute',
			left: 0,
			right: 0,
			top: u(207.5),
			textAlign: 'center',
		},
		bodyGrid: { rowGap: u(15) },
		asymmetricRow: { flexDirection: 'row', columnGap: u(12.5) },
		ageField: { width: u(100) },
		locationField: { flex: 1 },
		field: {
			backgroundColor: COLORS.surfaceRaised,
			borderColor: COLORS.borderOrange,
			borderRadius: u(15),
			padding: u(15),
			rowGap: u(5),
		},
		labelRow: {
			height: u(22.5),
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: u(10),
		},
		labelIcon: { width: u(17.5), height: u(17.5) },
		label: {
			color: COLORS.textSecondary,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: u(16),
			fontWeight: '600',
			lineHeight: u(19.2),
			includeFontPadding: false,
		},
		mutedLabel: { color: COLORS.textMuted },
		spacer: { flex: 1 },
		counter: {
			color: COLORS.textMuted,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: u(16),
			fontWeight: '500',
			lineHeight: u(22.4),
		},
		inputBox: {
			flexDirection: 'row',
			alignItems: 'center',
			columnGap: u(2.5),
			paddingHorizontal: u(12.5),
			paddingVertical: u(7.5),
			borderWidth: u(1.25),
			borderRadius: u(7.5),
			overflow: 'hidden',
		},
		inputFont,
		inputText: {
			...inputFont,
			flex: 1,
			minWidth: 0,
			height: u(INPUT_LINE_HEIGHT),
			padding: 0,
		},
		webInputReset: { outlineStyle: 'none', resize: 'none' } as any,
		lockedValue: { color: COLORS.textSecondary },
		measure: {
			position: 'absolute',
			left: u(12.5),
			right: u(12.5),
			top: 0,
			opacity: 0,
		},
		helper: {
			color: COLORS.textMuted,
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: u(16),
			fontWeight: '500',
			lineHeight: u(22.4),
		},
		dangerText: { color: COLORS.danger },
		submitError: {
			marginVertical: -u(10),
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: u(16),
			fontWeight: '500',
			lineHeight: u(22.4),
			textAlign: 'center',
		},
		actions: { flexDirection: 'row', columnGap: u(10) },
		action: {
			flex: 1,
			paddingVertical: u(15),
			borderRadius: u(12.5),
			borderWidth: u(1.25),
			alignItems: 'center',
			justifyContent: 'center',
			overflow: 'hidden',
		},
		actionInert: { backgroundColor: COLORS.surfaceRaised, borderColor: COLORS.borderSubtle },
		cancelLive: { backgroundColor: COLORS.surfaceRaised, borderColor: COLORS.borderOrange },
		saveLive: { borderColor: COLORS.saveRim, ...goldGlow },
		saveFill: { borderRadius: u(11.25), overflow: 'hidden' },
		actionBusy: { opacity: 0.55 },
		actionLabel: {
			fontFamily: ACHIEVEMENTS_FONT,
			fontSize: u(22),
			fontWeight: '900',
			lineHeight: u(26.4),
			letterSpacing: u(0.44),
			textTransform: 'uppercase',
		},
		inertLabel: { color: COLORS.textMuted },
		cancelLabel: { color: COLORS.textSecondary },
		saveLabel: { color: COLORS.textOnGold },
	});
};

export default ProfileRedesignEditDesktop;
